// src/components/PengaduanCard.jsx
import { useMemo, useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

function humanFileSize(bytes) {
  if (typeof bytes !== 'number' || Number.isNaN(bytes)) return null;
  const kb = bytes / 1024;
  const mb = kb / 1024;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${Math.round(kb)} KB`;
}

/**
 * @param {{
 *  data: {
 *    nama: string,
 *    email: string,
 *    telp?: string,
 *    contact?: string,
 *    imageUrl: string,      // URL publik struk, contoh: /assets/pengaduan/xxx.jpg
 *    filename?: string,     // opsional: nama file
 *    fileSize?: number,     // opsional: ukuran asli dalam byte
 *    createdAt?: string,    // opsional: timestamp
 *  }
 *  title?: string           // opsional: judul card
 * }} props
 */
export default function PengaduanCard({ data, title = '' }) {
  const [openPreview, setOpenPreview] = useState(false);
  const [imgError, setImgError] = useState(false);

  const {
    nama,
    email,
    telp,
    contact,
    imageUrl,
    filename,
    fileSize,
    createdAt,
  } = data || {};

  const phone = telp || contact || '—';
  const sizeLabel = useMemo(() => humanFileSize(fileSize), [fileSize]);

  // fallback kecil kalau img gagal
  const topMedia =
    !imgError && imageUrl ? (
      <CardMedia
        component="img"
        src={imageUrl}
        alt={`Struk ${filename || nama || 'pengaduan'}`}
        loading="lazy"
        onError={() => setImgError(true)}
        sx={{ maxHeight: 260, objectFit: 'cover', bgcolor: '#fafafa' }}
      />
    ) : (
      <Box
        sx={{
          height: 160,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          color: '#9e9e9e',
          fontSize: 14,
          letterSpacing: 0.4,
        }}
      >
        (Gambar struk tidak tersedia)
      </Box>
    );

  return (
    <>
      <Card variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
        {topMedia}

        <CardContent sx={{ p: 2.25 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {title}
          </Typography>

          <Stack spacing={1.25} divider={<Divider flexItem />}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Nama
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {nama || '—'}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {email || '—'}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Telepon
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {phone}
              </Typography>
            </Stack>

            {/* <Stack
              direction="row"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
            >
              <Typography variant="body2" color="text.secondary">
                File
              </Typography>
              <Stack direction="row" spacing={1}>
                <Typography variant="body2" fontWeight={600}>
                  {filename || (imageUrl ? imageUrl.split('/').pop() : '—')}
                </Typography>
                {sizeLabel && (
                  <Typography variant="body2" color="text.secondary">
                    ({sizeLabel})
                  </Typography>
                )}
              </Stack>
            </Stack> */}

            {createdAt && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Dibuat
                </Typography>
                <Typography variant="body2">
                  {dayjs(dayjs(createdAt).subtract(7, 'hours')).format(
                    'DD-MM-YYYY HH:mm:ss'
                  )}
                </Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>

        <CardActions sx={{ px: 2.25, pb: 2.25 }}>
          {imageUrl && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenPreview(true)}
            >
              Preview Struk
            </Button>
          )}
          {/* {imageUrl && (
            <Button
              component={Link}
              href={imageUrl}
              target="_blank"
              rel="noopener"
              variant="text"
              size="small"
              sx={{ ml: 'auto' }}
            >
              Buka Asli
            </Button>
          )} */}
        </CardActions>
      </Card>

      {/* Dialog Preview */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Preview Struk
          <IconButton
            onClick={() => setOpenPreview(false)}
            size="small"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {imageUrl ? (
            <Box sx={{ width: '100%', display: 'grid', placeItems: 'center' }}>
              <Box
                component="img"
                src={imageUrl}
                alt={`Struk ${filename || nama || ''}`}
                sx={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Gambar tidak tersedia.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
